import type { PalantirIaraAgent, PalantirIaraConversation } from '@flux/design-system';
import {
  CHAT_AGENT_ICON,
  CHAT_SESSION_FALLBACK_AGENT_NAME,
  CHAT_SESSION_FALLBACK_TITLE,
} from '../constants/chat-session-display';
import type { ChatAgent, ChatSession } from '../models/chat.model';

/**
 * Agente do backend no formato do design system.
 *
 * `role` fica vazio de propósito: o nome do agente já costuma trazer o papel embutido
 * ("Atlas | Gestão de tempo"), e a descrição vai para o campo truncável.
 */
export function toPalantirIaraAgent(agent: ChatAgent): PalantirIaraAgent {
  return {
    description: agent.description ?? undefined,
    icon: CHAT_AGENT_ICON,
    id: agent.id,
    name: agent.name,
    role: '',
  };
}

/**
 * Horário legível de uma sessão: hora no mesmo dia, data em dias anteriores.
 *
 * `now` é injetável para o teste ser determinístico — sem ele, o ramo "mesmo dia" não
 * dá para exercitar de forma estável.
 */
export function formatSessionTime(at?: string | null, now: Date = new Date()): string {
  if (!at) return '';

  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';

  return date.toDateString() === now.toDateString()
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/**
 * Sessão do `ChatService` no item de lista do design system.
 *
 * Função pura, e não método de componente, porque agora há DOIS consumidores: a página
 * `/chat` e a gaveta de histórico do painel flutuante.
 */
export function toIaraConversation(
  session: ChatSession,
  agents: readonly ChatAgent[] = [],
  now?: Date,
): PalantirIaraConversation {
  const agent = session.agentId
    ? (agents.find((candidate) => candidate.id === session.agentId) ?? null)
    : null;

  // Agente conhecido vira o formato do DS; desconhecido (não está na página carregada
  // de agentes) recebe um rótulo genérico em vez de sumir do item.
  const badge = agent
    ? toPalantirIaraAgent(agent)
    : session.agentId
      ? {
          icon: CHAT_AGENT_ICON,
          id: session.agentId,
          name: CHAT_SESSION_FALLBACK_AGENT_NAME,
          role: '',
        }
      : null;

  return {
    agent: badge,
    id: session.id,
    time: formatSessionTime(session.lastMessageAt ?? session.createdAt, now),
    title: session.title?.trim() || CHAT_SESSION_FALLBACK_TITLE,
  };
}
